import React, { useState } from 'react';
import { FieldProps, FieldValidation } from '@rjsf/core';
import { FormControl, Grid } from '@material-ui/core';
import { useForm, useFieldArray } from 'react-hook-form';
import './Extensions.css';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { appThemeApiRef, useApi } from '@backstage/core-plugin-api';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  aquaForm,
  cloudSecurityForm,
  securityExceptionValidator,
  snykForm,
  wizForm,
} from './securityExceptionFormUi';

type AquaFormValues = {
  fieldArray: {
    formID: number;
    name: string;
    applicationName: string;
    containerRegistryName: string;
    imageName: string;
    aquaSecurityPolicy: string;
    exemptionCategory: string;
    justification: string;
    duration: number;
  }[];
};

type WizFormValues = {
  fieldArray: {
    formID: number;
    name: string;
    applicationName: string;
    containerRegistryName: string;
    imageName: string;
    wizSecurityPolicy: string;
    otherSecurityPolicy: string;
    exemptionCategory: string;
    riskLvl: string;
    exemptionType: string;
    justification: string;
    duration: number;
  }[];
};

type CseFormValues = {
  fieldArray: {
    subscriptionName: number;
    resourceGroup: string;
    resourceName: string;
    cloudSecurityPolicy: string;
    exemptionCategory: number;
    exemptionType: string;
    justification: string;
    duration: string;
  }[];
};

type SnykFormValues = {
  fieldArray: {
    snykOrgId: string;
    snykProjectId: string;
    linkToIssue: string;
    exemptionCategory: string;
    exemptionType: string;
    justification: string;
    duration: number;
  }[];
};

export const AdditionalSecurityExemption = ({
  onChange,
}: FieldProps<string>) => {
  const { register, control, watch } = useForm<
    WizFormValues | AquaFormValues | CseFormValues | SnykFormValues
  >({
    defaultValues: {},
  });

  const [btnHover, setbtnHover] = useState({
    buttons: {
      wizBtn: {
        isHovered: false,
      },
      aquaBtn: {
        isHovered: false,
      },
      cseBtn: {
        isHovered: false,
      },
      snykBtn: {
        isHovered: false,
      },
    },
  });

  const themeApi = useApi(appThemeApiRef);

  const {
    fields,
    append,
    remove: removeParameters,
  } = useFieldArray({
    control,
    name: 'fieldArray',
  });
  const watchFieldArray = watch('fieldArray');
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  onChange({
    data: controlledFields,
  });

  const handleHover = (buttonToChange: string, hoverValue: boolean) => {
    setbtnHover(prevState => ({
      buttons: {
        ...prevState.buttons,
        [buttonToChange]: {
          ...prevState.buttons[buttonToChange],
          isHovered: hoverValue,
        },
      },
    }));
  };

  const deleteBtn = (appendName: string, btnName: string, btnTitle: string) => {
    return (
      <button
        onClick={() =>
          append({
            name: appendName,
          })
        }
        onMouseEnter={() => {
          handleHover(btnName, true);
        }}
        onMouseLeave={() => {
          handleHover(btnName, false);
        }}
        style={{
          marginRight: '10px',
          backgroundColor:
            btnHover.buttons[btnName].isHovered === false
              ? '#64D9DB'
              : '#A9F6F8',
          marginTop: '30px',
          fontWeight: '400',
          fontSize: '1.175rem',
          fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          borderRadius: '10px',
          padding: '10px',
          color:
            themeApi.getActiveThemeId() === 'dark-theme' ? 'black' : 'white',
        }}
      >
        {btnTitle}
      </button>
    );
  };

  return (
    <FormControl>
      <form>
        <Grid container direction="column">
          {controlledFields.map((field, index) => {
            field.formID = index;

            const removeForm = () => {
              return (
                <Button
                  style={{
                    marginTop: '15px',
                    backgroundColor: 'red',
                  }}
                  variant="contained"
                  onClick={() => removeParameters(index)}
                >
                  <DeleteIcon />
                </Button>
              );
            };

            const form = (
              childrenForm: any,
              fieldID: string,
              title: string,
            ) => {
              return (
                <Grid container>
                  <Grid item xs={1}>
                    {removeForm()}
                  </Grid>
                  <Grid item xs={11}>
                    <Accordion
                      defaultExpanded
                      disableGutters
                      elevation={1}
                      sx={{
                        backgroundColor:
                          themeApi.getActiveThemeId() === 'dark-theme'
                            ? '#2f2d2da6'
                            : 'white',
                        color:
                          themeApi.getActiveThemeId() === 'dark-theme'
                            ? 'white'
                            : 'black',
                        fontWeight: 'bold',
                        marginBottom: 'px',
                        marginTop: '10px',
                        marginRight: '40px',
                        margineLeft: '10px',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`${fieldID}-content`}
                        id={`${fieldID}-header`}
                      >
                        <Typography>{`${title}: ${fieldID}`}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <FormControl>{childrenForm}</FormControl>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              );
            };

            function renderForm(name: string, id: string) {
              switch (name) {
                case 'aqua':
                  return form(
                    aquaForm(
                      register(`fieldArray.${index}.applicationName` as const),
                      register(
                        `fieldArray.${index}.containerRegistryName` as const,
                      ),
                      register(`fieldArray.${index}.imageName` as const),
                      register(
                        `fieldArray.${index}.aquaSecurityPolicy` as const,
                      ),
                      register(
                        `fieldArray.${index}.exemptionCategory` as const,
                      ),
                      register(`fieldArray.${index}.justification` as const),
                      register(`fieldArray.${index}.duration` as const),
                      themeApi.getActiveThemeId(),
                    ),
                    id,
                    'Aqua Exception Submission',
                  );
                case 'wiz': {
                  const wizField = field as WizFormValues['fieldArray'][number];

                  return form(
                    wizForm(
                      register(`fieldArray.${index}.applicationName` as const),
                      register(
                        `fieldArray.${index}.containerRegistryName` as const,
                      ),
                      register(`fieldArray.${index}.imageName` as const),
                      register(
                        `fieldArray.${index}.wizSecurityPolicy` as const,
                      ),
                      register(
                        `fieldArray.${index}.otherSecurityPolicy` as const,
                      ),
                      register(
                        `fieldArray.${index}.exemptionCategory` as const,
                      ),
                      register(`fieldArray.${index}.riskLvl` as const),
                      register(`fieldArray.${index}.exemptionType` as const),
                      register(`fieldArray.${index}.justification` as const),
                      register(`fieldArray.${index}.duration` as const),
                      wizField.wizSecurityPolicy,
                      wizField.exemptionType,
                      themeApi.getActiveThemeId(),
                    ),
                    id,
                    'WIZ Exception Submission',
                  );
                }
                case 'cse':
                  return form(
                    cloudSecurityForm(
                      register(`fieldArray.${index}.subscriptionName` as const),
                      register(`fieldArray.${index}.resourceGroup` as const),
                      register(`fieldArray.${index}.resourceName` as const),
                      register(
                        `fieldArray.${index}.cloudSecurityPolicy` as const,
                      ),
                      register(
                        `fieldArray.${index}.exemptionCategory` as const,
                      ),
                      register(`fieldArray.${index}.exemptionType` as const),
                      register(`fieldArray.${index}.justification` as const),

                      register(`fieldArray.${index}.duration` as const),
                      themeApi.getActiveThemeId(),
                    ),
                    id,
                    'Cloud Security Exception Submission',
                  );
                case 'snyk':
                  return form(
                    snykForm(
                      register(`fieldArray.${index}.snykOrgId` as const),
                      register(`fieldArray.${index}.snykProjectId` as const),
                      register(`fieldArray.${index}.linkToIssue` as const),
                      register(
                        `fieldArray.${index}.exemptionCategory` as const,
                      ),
                      register(`fieldArray.${index}.exemptionType` as const),
                      register(`fieldArray.${index}.justification` as const),
                      register(`fieldArray.${index}.duration` as const),
                      themeApi.getActiveThemeId(),
                    ),
                    id,
                    'Snyk Exception Submission',
                  );
                default:
                  return null;
              }
            }

            return renderForm(field.name, field.id);
          })}

          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            {deleteBtn('wiz', 'wizBtn', 'Add WIZ Exception')}
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            {deleteBtn('aqua', 'aquaBtn', 'Add Aqua Exception')}
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            {deleteBtn('cse', 'cseBtn', 'Add Cloud Security Exception')}
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            {deleteBtn('snyk', 'snykBtn', 'Add Snyk Exception')}
          </Grid>
        </Grid>
      </form>
    </FormControl>
  );
};
export const addAdditionalSecurityExemptionValidation = (
  value: WizFormValues | AquaFormValues | CseFormValues | SnykFormValues,
  validation: FieldValidation,
) => {
  if (JSON.stringify(value) !== '{}') {
    if (value.data.length > 0) {
      value.data.map(formData => {
        securityExceptionValidator(formData, validation);
      });
    }
  }
};
